import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  OctahedronGeometry,
  SphereGeometry,
  TorusGeometry,
  type BufferGeometry,
} from 'three';
import { MATERIALS, applyContactShading, tintVertexColor } from '../render/materials';
import { EMIT } from '../render/palette';
import { lightPool } from './props';
import { ITEMS } from '../sim/items';
import type { ItemId } from '../sim/types';

/**
 * A physical object for every item in the game.
 *
 * Everything in `items.ts` can end up on the floor — dropped on purpose, spilled
 * by a guard's hand on your shoulder, or laid out on the tool bench at the start
 * of the round — and an invisible pickup prompt floating over bare tile is the
 * cheapest-feeling thing a game can do. These are small, deliberately readable
 * silhouettes: you should know a crowbar from a grinder from across a dark
 * concourse, because choosing between them is the loadout decision the whole
 * round hangs on.
 *
 * A faint additive pool goes under each one. A dropped diamond is two
 * centimetres across in a building lit at a median of 40/255; without the pool
 * it is genuinely unfindable, and "I dropped it somewhere here" is not a
 * mechanic anyone enjoys.
 */

const CARD = () => MATERIALS.card();
const STEEL = () => MATERIALS.steel();
const DARK = () => MATERIALS.steelDark();
const RUBBER = () => MATERIALS.rubber();
const GLASSY = () => MATERIALS.gloss();

/** Colour per item, so a heap of spilled loot still reads as distinct things. */
const TINT: Record<string, number> = {
  crowbar: 0xc4442e,
  lockpick: 0x2b3340,
  grinder: 0xe0a52c,
  keycard: 0x2f7fd0,
  drill: 0xd8542a,
  energyDrink: 0x8fe04a,
  painkillers: 0xe8e8ec,
  jammer: 0x39424e,
  feltPads: 0x9a7c5a,
  diamonds: 0xbfe8ff,
  ring: 0xf2dd9a,
  watch: 0xd8dde4,
  goldChain: 0xe6c15a,
  opioids: 0xdfe6ee,
  insulin: 0xa9d8e8,
  vitamins: 0xd9a544,
  phone: 0x2a3038,
  laptop: 0x9aa3ad,
  drone: 0x33383f,
  console: 0x232830,
  television: 0x1b1f26,
  pistol: 0x2c3138,
  rifle: 0x5a4331,
  ammoCase: 0x4c5340,
  longsword: 0xa8b0ba,
  halberd: 0x8a745a,
  armour: 0x9ba4b0,
  coat: 0x3d4756,
  sneakers: 0xe4e7ea,
  perfume: 0xe0a8c0,
  firstEdition: 0x7a4a34,
  till: 0x394250,
  safeCash: 0x7fa86a,
  steak: 0xb04a48,
  espressoMachine: 0xb9bfc6,
  vacuum: 0x6a4f8c,
  skateDeck: 0xd8b24a,
};

/**
 * The dropped/lying-on-the-floor form of an item, with its origin at the floor.
 *
 * Not memoised. Each instance is positioned independently and the additive pool
 * has to be its own mesh, and there are at most a few dozen of these alive at
 * once — this is not the supermarket.
 */
export function lootProp(id: ItemId): Group {
  const g = new Group();
  g.name = `loot:${id}`;
  g.add(lootBody(id));

  const def = ITEMS[id];
  // The marker scales with what the thing is worth to you, not with its size.
  // A bag of banded cash on the floor should pull you across the room; a tub of
  // vitamins should not.
  const pull = def.kind === 'tool' ? 0.62 : def.value >= 2000 ? 0.7 : def.value >= 500 ? 0.5 : 0.34;
  const colour = def.kind === 'tool' ? EMIT.signCyan : def.value >= 2000 ? EMIT.shopWarm : EMIT.fluoro;
  const pool = lightPool(pull, colour, def.kind === 'tool' ? 0.5 : 0.42, `loot:${id}`);
  pool.position.y = 0.014;
  g.add(pool);
  return g;
}

/**
 * The object itself, with no floor marker under it.
 *
 * `lootProp` is the thing lying on the tiles and it needs the pool to be
 * findable. The same geometry held in a fist thirty centimetres from the camera
 * emphatically does not — an additive quad through the middle of your own hand
 * is a lens flare, not a light. Origin and orientation are unchanged: the object
 * still sits on a floor at y=0 with its long axis along X, and the viewmodel's
 * grip table is written against that.
 */
export function lootBody(id: ItemId): Group {
  return BUILD[id]?.() ?? genericParcel(id);
}

function part(geo: BufferGeometry, mat: () => THREE_MAT, tint?: number): Mesh {
  applyContactShading(geo, { falloff: 0.4, strength: 0.35 });
  if (tint !== undefined) tintVertexColor(geo, tint);
  return new Mesh(geo, mat());
}

type THREE_MAT = ReturnType<typeof MATERIALS.card>;

/** Fallback: a taped parcel, sized and coloured from the item's own numbers. */
function genericParcel(id: ItemId): Group {
  const def = ITEMS[id];
  const g = new Group();
  // Cube root of weight gives a box that looks like it weighs what it weighs —
  // a television is visibly a two-person carry and a ring is a speck.
  const s = Math.min(0.62, Math.max(0.09, Math.cbrt(Math.max(0.02, def.weight)) * 0.24));
  g.add(offset(part(new BoxGeometry(s * 1.3, s, s * 0.9), CARD, TINT[id] ?? 0xb4b9c0), 0, s / 2, 0));
  return g;
}

const BUILD: Partial<Record<ItemId, () => Group>> = {
  // --- tools --------------------------------------------------------------
  /**
   * A wrecking bar, not two boxes.
   *
   * Every tool below got rebuilt when the viewmodel landed, because the distance
   * these are read at changed by two orders of magnitude. A crowbar across a dark
   * concourse needs a silhouette; a crowbar held forty centimetres from the eye,
   * turned slowly in front of your face during a pickup, needs to be a crowbar —
   * a hexagonal shaft, a goose-neck that actually curves, a split claw with a nail
   * slot in it, and paint worn back to bare steel at both ends because those are
   * the two ends that get hit.
   *
   * Long axis along X with the claw at -X. The viewmodel's grip table is written
   * against that and turns it the other way from the rest of these on purpose.
   */
  crowbar: () => {
    const g = new Group();
    const RED = TINT.crowbar;
    const BARE = 0x8b939c;

    // Hexagonal shaft. Six sides, not round: the flats catch the key light one at
    // a time as the bar turns, which is most of what sells it as forged steel.
    const shaft = part(new CylinderGeometry(0.021, 0.026, 0.86, 6), CARD, RED);
    shaft.position.set(0.045, 0.03, 0);
    shaft.rotation.z = Math.PI / 2;
    g.add(shaft);

    // Goose-neck: five short segments walking an arc up and back over the claw.
    // Explicit rather than a curve — the faceting between segments is the look.
    let cx = -0.385;
    let cy = 0.03;
    let ang = 0;
    for (let i = 0; i < 5; i++) {
      const step = 0.038;
      const turn = 0.26 + i * 0.06;
      ang += turn;
      const seg = part(new CylinderGeometry(0.019, 0.021, step * 1.25, 6), i > 2 ? STEEL : CARD, i > 2 ? BARE : RED);
      cx -= Math.cos(ang) * step;
      cy += Math.sin(ang) * step;
      seg.position.set(cx, cy, 0);
      seg.rotation.z = Math.PI / 2 - ang;
      g.add(seg);
    }

    // The claw itself: two flattened prongs with a nail slot between them.
    for (const z of [-0.021, 0.021]) {
      const prong = part(new BoxGeometry(0.075, 0.014, 0.024), STEEL, BARE);
      prong.position.set(cx - 0.028, cy + 0.014, z);
      prong.rotation.z = Math.PI / 2 - ang - 0.35;
      g.add(prong);
    }

    // Chisel end. Flattened and bevelled, worn back to bare metal.
    const chisel = part(new BoxGeometry(0.095, 0.012, 0.05), STEEL, BARE);
    chisel.position.set(0.5, 0.03, 0);
    chisel.rotation.z = -0.13;
    g.add(chisel);

    // A knurled grip band two thirds of the way down, where a hand goes.
    const band = part(new CylinderGeometry(0.029, 0.029, 0.11, 8), RUBBER, 0x1d2026);
    band.position.set(0.23, 0.03, 0);
    band.rotation.z = Math.PI / 2;
    g.add(band);
    return g;
  },

  /**
   * A leather pick roll, open, with the picks actually distinguishable.
   *
   * The tension wrench is the tell — it is the one piece in the roll bent at a
   * right angle, and anybody who has seen a lock picked once recognises it.
   */
  lockpick: () => {
    const g = new Group();
    const HIDE = TINT.lockpick;
    const WIRE = 0xc8ced6;

    // The roll, unfolded: a backing panel with a raised lip of pockets.
    g.add(offset(part(new BoxGeometry(0.22, 0.012, 0.115), CARD, HIDE), 0, 0.006, 0));
    g.add(offset(part(new BoxGeometry(0.21, 0.016, 0.03), CARD, 0x222a36), -0.005, 0.02, 0.04));
    // Folded-back flap, standing off the panel at an angle.
    g.add(offset(part(new BoxGeometry(0.2, 0.008, 0.07), CARD, 0x1c2330), -0.005, 0.036, -0.062, -0.75, 0, 0));
    // Tie strap across the middle.
    g.add(offset(part(new BoxGeometry(0.012, 0.02, 0.12), CARD, 0x3c3226), 0.06, 0.014, 0));

    // Five picks, each with a moulded handle and its own tip. A rake, a hook, a
    // half-diamond, a ball, and the wrench.
    const TIPS: Array<[number, number, number]> = [
      [0.052, 0.004, 0.004],
      [0.05, 0.0055, 0.0035],
      [0.046, 0.007, 0.0035],
      [0.048, 0.005, 0.005],
      [0.03, 0.005, 0.004],
    ];
    for (let i = 0; i < 5; i++) {
      const z = -0.036 + i * 0.019;
      const yaw = (i - 2) * 0.1;
      const handle = part(new BoxGeometry(0.058, 0.008, 0.011), CARD, 0x2f3a48);
      handle.position.set(-0.03, 0.026, z);
      handle.rotation.y = yaw;
      g.add(handle);
      const [len, h, w] = TIPS[i];
      const wire = part(new BoxGeometry(len, h, w), STEEL, WIRE);
      wire.position.set(0.03 + len / 2 - 0.02, 0.026, z + Math.sin(yaw) * 0.05);
      wire.rotation.y = yaw;
      g.add(wire);
    }
    // The wrench's foot, bent down at ninety degrees off the last pick.
    g.add(offset(part(new BoxGeometry(0.005, 0.018, 0.004), STEEL, WIRE), 0.042, 0.018, 0.038));
    return g;
  },

  /**
   * An angle grinder with a guard, which is the part that makes it read as a
   * power tool rather than a torch: a bare disc on a stick is a lollipop.
   */
  grinder: () => {
    const g = new Group();
    const BODY = TINT.grinder;

    // The axis sits thirteen centimetres up because the disc is 224mm across and
    // the guard is wider still: a grinder put down on a floor rests on the rim of
    // its guard, not on its barrel. Authored at barrel height the disc cut a
    // six-centimetre hole through the tiles.

    // Motor barrel, then a narrower gearcase stepping down to the spindle.
    g.add(offset(part(new CylinderGeometry(0.052, 0.056, 0.24, 12), CARD, BODY), -0.02, 0.135, 0, 0, 0, Math.PI / 2));
    g.add(offset(part(new CylinderGeometry(0.043, 0.05, 0.075, 10), CARD, 0x2c3138), 0.135, 0.135, 0, 0, 0, Math.PI / 2));
    // Rear cap and the cord gland behind it.
    g.add(offset(part(new CylinderGeometry(0.05, 0.046, 0.03, 10), RUBBER, 0x1c2026), -0.155, 0.135, 0, 0, 0, Math.PI / 2));
    g.add(offset(part(new CylinderGeometry(0.013, 0.016, 0.055, 6), RUBBER, 0x15181d), -0.195, 0.13, 0, 0, 0, Math.PI / 2));

    // Cutting disc, and the spindle nut clamping it.
    // Abrasive, not steel, and lifted well off black: at metalness 0.68 and a
    // tint this dark the disc rendered as a hole punched in the frame.
    const disc = part(new CylinderGeometry(0.112, 0.112, 0.004, 20), CARD, 0x6b5f56);
    disc.position.set(0.198, 0.135, 0);
    disc.rotation.set(Math.PI / 2, 0, Math.PI / 2);
    g.add(disc);
    g.add(offset(part(new CylinderGeometry(0.022, 0.022, 0.014, 8), STEEL, 0x9aa2ab), 0.206, 0.135, 0, 0, 0, Math.PI / 2));

    // Guard: a half-shell over the back of the disc, with a lip around its rim.
    const shell = part(new CylinderGeometry(0.125, 0.125, 0.004, 18, 1, false, Math.PI * 0.05, Math.PI * 1.05), DARK, 0x7d848c);
    shell.position.set(0.182, 0.135, 0);
    shell.rotation.set(Math.PI / 2, 0, Math.PI / 2);
    g.add(shell);
    const lip = part(new TorusGeometry(0.124, 0.011, 4, 16, Math.PI * 1.05), DARK, 0x7d848c);
    lip.position.set(0.176, 0.135, 0);
    lip.rotation.set(0, Math.PI / 2, Math.PI * 0.05);
    g.add(lip);

    // Side handle screwed into the gearcase, and the paddle switch underneath.
    g.add(offset(part(new CylinderGeometry(0.017, 0.02, 0.11, 8), RUBBER, 0x23272d), 0.115, 0.17, 0.06, Math.PI / 2.3, 0, 0));
    g.add(offset(part(new BoxGeometry(0.11, 0.014, 0.045), CARD, 0x1f2329), -0.03, 0.087, 0));
    return g;
  },

  /**
   * A proximity card. Flat, so nearly all of it is surface detail — a magstripe,
   * a chip, a punched slot and a printed band are what separate it from a chip
   * of blue plastic.
   */
  keycard: () => {
    const g = new Group();
    g.add(offset(part(new BoxGeometry(0.086, 0.0028, 0.054), CARD, TINT.keycard), 0, 0.0045, 0));
    // Printed header band across the top third.
    g.add(offset(part(new BoxGeometry(0.086, 0.0006, 0.014), CARD, 0x11305c), 0, 0.0061, -0.019));
    // Gold contact chip.
    g.add(offset(part(new BoxGeometry(0.013, 0.0008, 0.011), CARD, 0xd8c47a), -0.024, 0.0062, 0.004));
    // Magstripe on the reverse.
    g.add(offset(part(new BoxGeometry(0.086, 0.0008, 0.011), CARD, 0x14181e), 0, 0.0026, 0.014));
    // Punched lanyard slot, drawn as the two bridges either side of the hole.
    for (const z of [-0.008, 0.008]) {
      g.add(offset(part(new BoxGeometry(0.012, 0.0028, 0.013), CARD, TINT.keycard), 0.037, 0.0045, z));
    }
    return g;
  },

  /**
   * A cordless drill: motor barrel, pistol grip, slide battery, keyless chuck.
   * The chuck is three stepped rings rather than one cylinder, because a chuck
   * that does not step reads as a pipe.
   */
  drill: () => {
    const g = new Group();
    const BODY = TINT.drill;
    const DARKB = 0x2c3138;

    // Motor barrel and the gearcase collar in front of it.
    g.add(offset(part(new CylinderGeometry(0.052, 0.055, 0.2, 12), CARD, BODY), -0.01, 0.135, 0, 0, 0, Math.PI / 2));
    g.add(offset(part(new CylinderGeometry(0.046, 0.05, 0.045, 10), CARD, DARKB), 0.112, 0.135, 0, 0, 0, Math.PI / 2));
    // Rubber overmould along the top of the barrel.
    g.add(offset(part(new BoxGeometry(0.15, 0.016, 0.055), RUBBER, 0x1b1f24), -0.02, 0.182, 0));

    // Keyless chuck: three steps, knurled sleeve in the middle.
    g.add(offset(part(new CylinderGeometry(0.032, 0.038, 0.02, 10), STEEL, 0x767e88), 0.145, 0.135, 0, 0, 0, Math.PI / 2));
    g.add(offset(part(new CylinderGeometry(0.034, 0.034, 0.042, 14), STEEL, 0x9aa2ab), 0.172, 0.135, 0, 0, 0, Math.PI / 2));
    g.add(offset(part(new CylinderGeometry(0.019, 0.028, 0.018, 10), STEEL, 0x767e88), 0.2, 0.135, 0, 0, 0, Math.PI / 2));
    // The bit, tapering to a point.
    g.add(offset(part(new CylinderGeometry(0.008, 0.011, 0.17, 8), STEEL, 0x5d666f), 0.29, 0.135, 0, 0, 0, Math.PI / 2));
    g.add(offset(part(new CylinderGeometry(0.0, 0.008, 0.022, 8), STEEL, 0xb2b9c0), 0.386, 0.135, 0, 0, 0, -Math.PI / 2));

    // Pistol grip, raked back, with a trigger and the forward LED.
    g.add(offset(part(new BoxGeometry(0.058, 0.14, 0.05), CARD, BODY), -0.055, 0.062, 0, 0, 0, 0.16));
    g.add(offset(part(new BoxGeometry(0.02, 0.048, 0.038), RUBBER, 0x14181d), -0.024, 0.093, 0));
    g.add(offset(part(new BoxGeometry(0.01, 0.008, 0.01), CARD, 0xe8f0d8), 0.078, 0.1, 0));

    // Slide battery: wider than the grip, with a release catch and a fuel gauge.
    g.add(offset(part(new BoxGeometry(0.098, 0.05, 0.072), CARD, DARKB), -0.075, 0.024, 0));
    g.add(offset(part(new BoxGeometry(0.02, 0.014, 0.05), CARD, 0x4c545e), -0.122, 0.038, 0));
    g.add(offset(part(new BoxGeometry(0.026, 0.004, 0.01), CARD, 0x6fd05a), -0.06, 0.05, 0.026));
    return g;
  },

  // --- consumables --------------------------------------------------------
  energyDrink: () => {
    const g = new Group();
    g.add(offset(part(new CylinderGeometry(0.033, 0.033, 0.13, 14), CARD, TINT.energyDrink), 0, 0.068, 0));
    // Printed band and the shoulder taper into a narrower lid.
    g.add(offset(part(new CylinderGeometry(0.0335, 0.0335, 0.05, 14), CARD, 0x1d2a14), 0, 0.06, 0));
    g.add(offset(part(new CylinderGeometry(0.024, 0.033, 0.018, 14), CARD, 0xb9c3cc), 0, 0.142, 0));
    g.add(offset(part(new CylinderGeometry(0.026, 0.026, 0.005, 14), CARD, 0xd2d9df), 0, 0.153, 0));
    g.add(offset(part(new BoxGeometry(0.016, 0.002, 0.008), CARD, 0x9aa3ac), 0.006, 0.157, 0));
    return g;
  },
  painkillers: () => {
    const g = new Group();
    g.add(offset(part(new BoxGeometry(0.095, 0.048, 0.055), CARD, TINT.painkillers), 0, 0.024, 0));
    // Printed panel and the cross, so it reads as medicine and not as soap.
    g.add(offset(part(new BoxGeometry(0.06, 0.0012, 0.03), CARD, 0xc03a3a), -0.012, 0.049, 0));
    g.add(offset(part(new BoxGeometry(0.02, 0.0016, 0.006), CARD, 0xffffff), 0.026, 0.049, 0));
    g.add(offset(part(new BoxGeometry(0.006, 0.0016, 0.02), CARD, 0xffffff), 0.026, 0.049, 0));
    // A blister strip half out of the end, blisters and all.
    const strip = part(new BoxGeometry(0.05, 0.004, 0.045), CARD, 0xc8ccd2);
    strip.position.set(0.068, 0.014, 0);
    g.add(strip);
    for (let i = 0; i < 3; i++) {
      g.add(offset(part(new SphereGeometry(0.0055, 6, 4), CARD, 0xe8ecf0), 0.056 + i * 0.012, 0.018, 0.011));
      g.add(offset(part(new SphereGeometry(0.0055, 6, 4), CARD, 0xe8ecf0), 0.056 + i * 0.012, 0.018, -0.011));
    }
    return g;
  },
  opioids: () => {
    const g = new Group();
    // Amber pill bottle with a child-proof cap and a wrapped label.
    g.add(offset(part(new CylinderGeometry(0.026, 0.024, 0.075, 12), CARD, 0xb07a32), 0, 0.038, 0));
    g.add(offset(part(new CylinderGeometry(0.0265, 0.0265, 0.036, 12), CARD, TINT.opioids), 0, 0.032, 0));
    g.add(offset(part(new CylinderGeometry(0.028, 0.028, 0.016, 14), CARD, 0xd8dce2), 0, 0.083, 0));
    // Ribs on the cap, because a smooth cap is a cork.
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      g.add(offset(part(new BoxGeometry(0.004, 0.016, 0.008), CARD, 0xc2c7cd), Math.cos(a) * 0.028, 0.083, Math.sin(a) * 0.028, 0, -a, 0));
    }
    return g;
  },
  insulin: () => {
    const g = new Group();
    // A chilled pack: carton, two glass vials, one pen.
    g.add(offset(part(new BoxGeometry(0.11, 0.045, 0.07), CARD, 0xe6eef2), 0, 0.022, 0));
    g.add(offset(part(new BoxGeometry(0.11, 0.0014, 0.024), CARD, TINT.insulin), 0, 0.045, -0.018));
    for (const z of [-0.016, 0.016]) {
      g.add(offset(part(new CylinderGeometry(0.011, 0.011, 0.05, 10), GLASSY, 0xcfe6ef), 0.078, 0.025, z));
      g.add(offset(part(new CylinderGeometry(0.012, 0.012, 0.008, 10), CARD, 0x9fb6c2), 0.078, 0.053, z));
    }
    g.add(offset(part(new CylinderGeometry(0.009, 0.009, 0.13, 10), CARD, 0xdfe6ee), -0.02, 0.055, 0.05, 0, 0, Math.PI / 2));
    g.add(offset(part(new CylinderGeometry(0.005, 0.009, 0.025, 8), CARD, 0x5a8fa8), 0.058, 0.055, 0.05, 0, 0, Math.PI / 2));
    return g;
  },
  vitamins: () => {
    const g = new Group();
    g.add(offset(part(new CylinderGeometry(0.042, 0.04, 0.095, 14), CARD, TINT.vitamins), 0, 0.048, 0));
    g.add(offset(part(new CylinderGeometry(0.0425, 0.0425, 0.05, 14), CARD, 0xf0e4c4), 0, 0.044, 0));
    g.add(offset(part(new CylinderGeometry(0.044, 0.044, 0.02, 14), CARD, 0x6b4f22), 0, 0.105, 0));
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      g.add(offset(part(new BoxGeometry(0.005, 0.02, 0.008), CARD, 0x5c4520), Math.cos(a) * 0.044, 0.105, Math.sin(a) * 0.044, 0, -a, 0));
    }
    return g;
  },
  jammer: () => {
    const g = new Group();
    g.add(offset(part(new BoxGeometry(0.13, 0.055, 0.095), CARD, TINT.jammer), 0, 0.03, 0));
    // Rubber end bumpers, a belt clip, two aerials and a live indicator.
    for (const x of [-0.068, 0.068]) {
      g.add(offset(part(new BoxGeometry(0.012, 0.06, 0.1), RUBBER, 0x15181c), x, 0.03, 0));
    }
    g.add(offset(part(new BoxGeometry(0.05, 0.006, 0.03), DARK, 0x2b3138), -0.02, 0.06, -0.052, 0.35, 0, 0));
    for (const [x, tilt] of [[0.045, 0.18], [-0.045, -0.14]] as const) {
      g.add(offset(part(new CylinderGeometry(0.0045, 0.006, 0.15, 6), DARK, 0x1a1d22), x, 0.13, 0, 0, 0, tilt));
      g.add(offset(part(new SphereGeometry(0.007, 6, 4), RUBBER, 0x101215), x + Math.sin(tilt) * 0.075, 0.203, 0));
    }
    g.add(offset(part(new BoxGeometry(0.008, 0.003, 0.008), CARD, 0x64e08a), 0.03, 0.058, 0.03));
    g.add(offset(part(new BoxGeometry(0.03, 0.004, 0.018), DARK, 0x11141a), -0.03, 0.058, 0.028));
    return g;
  },
  feltPads: () => {
    const g = new Group();
    // A backing card with the pads still half attached to it.
    g.add(offset(part(new BoxGeometry(0.11, 0.003, 0.085), CARD, 0xd8cdb6), 0, 0.0015, 0));
    for (let i = 0; i < 4; i++) {
      const d = part(new CylinderGeometry(0.023, 0.023, 0.009, 12), RUBBER, TINT.feltPads);
      d.position.set((i % 2) * 0.05 - 0.025, 0.008 + (i === 3 ? 0.009 : 0), Math.floor(i / 2) * 0.048 - 0.024);
      if (i === 3) d.rotation.x = 0.4;
      g.add(d);
    }
    return g;
  },

  // --- jewellery ----------------------------------------------------------
  diamonds: () => {
    const g = new Group();
    // A velvet pouch, slumped, with the drawstring loose and stones tipped out.
    // Squashed, not spherical: a velvet bag with nothing left in it slumps.
    const pouch = offset(part(new SphereGeometry(0.045, 8, 6), CARD, 0x1e2430), -0.035, 0.03, 0, 0, 0, 0.2);
    pouch.scale.set(1, 0.62, 0.85);
    g.add(pouch);
    g.add(offset(part(new CylinderGeometry(0.022, 0.03, 0.03, 8), CARD, 0x161b25), -0.045, 0.055, 0, 0, 0, 0.35));
    g.add(offset(part(new TorusGeometry(0.021, 0.0035, 4, 10), CARD, 0x6b5f3e), -0.047, 0.066, 0, Math.PI / 2, 0, 0.35));
    // Three brilliants: a crown of facets over a pointed pavilion. An octahedron
    // is the one primitive that throws a different value off every face, which
    // is the entire reason a cut stone looks like a cut stone.
    const AT: Array<[number, number, number]> = [
      [0.038, 0.016, 0.014],
      [0.07, 0.013, -0.02],
      [0.048, 0.011, -0.038],
    ];
    for (let i = 0; i < 3; i++) {
      const [x, s, z] = AT[i];
      const stone = part(new OctahedronGeometry(s, 0), CARD, TINT.diamonds);
      stone.position.set(x, s * 0.85, z);
      stone.rotation.set(0.3 + i * 0.4, i * 0.8, 0.2);
      g.add(stone);
      // A flat table ground onto the top, the way a real cut is.
      const table = part(new CylinderGeometry(s * 0.42, s * 0.42, 0.001, 8), CARD, 0xe8f6ff);
      table.position.set(x, s * 1.55, z);
      g.add(table);
    }
    return g;
  },
  ring: () => {
    const g = new Group();
    // Sitting in the slot of a display cushion, which is how it left the case.
    g.add(offset(part(new BoxGeometry(0.05, 0.016, 0.038), CARD, 0x1c222c), 0, 0.008, 0));
    const band = part(new TorusGeometry(0.014, 0.0035, 6, 16), CARD, TINT.ring);
    band.position.y = 0.03;
    g.add(band);
    // Four prongs holding a stone up off the shank.
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + 0.78;
      g.add(offset(part(new BoxGeometry(0.0022, 0.009, 0.0022), CARD, TINT.ring), Math.cos(a) * 0.006, 0.047, Math.sin(a) * 0.006));
    }
    const stone = part(new OctahedronGeometry(0.0085, 0), CARD, TINT.diamonds);
    stone.position.y = 0.054;
    stone.rotation.set(0.4, 0.5, 0);
    g.add(stone);
    return g;
  },
  watch: () => {
    const g = new Group();
    // Case, bezel, dial, crown, and a strap in two halves with a buckle — a
    // puck on a ribbon is a coaster. The strap curls up rather than lying flat:
    // a watch put down keeps the shape of the wrist it came off, and flat
    // straps photographed from the side make the whole thing a two-millimetre
    // line on the tiles.
    g.add(offset(part(new CylinderGeometry(0.021, 0.02, 0.011, 16), CARD, TINT.watch), 0, 0.028, 0));
    g.add(offset(part(new TorusGeometry(0.02, 0.003, 4, 18), CARD, 0xb9c1ca), 0, 0.034, 0, Math.PI / 2, 0, 0));
    g.add(offset(part(new CylinderGeometry(0.0165, 0.0165, 0.001, 16), CARD, 0x12161c), 0, 0.034, 0));
    for (let i = 0; i < 2; i++) {
      const a = i === 0 ? 0.6 : 2.3;
      g.add(offset(part(new BoxGeometry(0.011, 0.0008, 0.0016), CARD, 0xdfe4ea), Math.cos(a) * 0.005, 0.0352, Math.sin(a) * 0.005, 0, -a, 0));
    }
    g.add(offset(part(new CylinderGeometry(0.0035, 0.0035, 0.006, 8), CARD, 0xb9c1ca), 0.023, 0.028, 0, 0, 0, Math.PI / 2));
    // Each strap curves down off the lug and then flattens onto the tiles.
    for (const dir of [-1, 1]) {
      let z = dir * 0.021;
      let y = 0.03;
      for (const ang of [0.45, 0.95]) {
        g.add(offset(part(new BoxGeometry(0.015, 0.0045, 0.026), CARD, 0x2a2f36), 0, y, z, dir * ang, 0, 0));
        z += dir * Math.cos(ang) * 0.02;
        y -= Math.sin(ang) * 0.02;
      }
      g.add(offset(part(new BoxGeometry(0.015, 0.0045, 0.05), CARD, 0x2a2f36), 0, 0.0035, z + dir * 0.024));
      if (dir > 0) g.add(offset(part(new BoxGeometry(0.016, 0.005, 0.014), CARD, 0xa8b0b9), 0, 0.005, z + 0.055));
    }
    return g;
  },
  goldChain: () => {
    const g = new Group();
    // Poured into a heap. Links at alternating angles, because a stack of
    // co-planar rings is a pile of washers.
    for (let i = 0; i < 9; i++) {
      const a = i * 1.9;
      const link = part(new TorusGeometry(0.011, 0.0028, 4, 10), CARD, TINT.goldChain);
      link.position.set(Math.cos(a) * (0.006 + i * 0.0026), 0.004 + (i % 3) * 0.004, Math.sin(a) * (0.006 + i * 0.0026));
      link.rotation.set(Math.PI / 2 + (i % 2) * 0.9, a, 0);
      g.add(link);
    }
    // A pendant, so it reads as something worn rather than as stock.
    const tag = part(new BoxGeometry(0.016, 0.0035, 0.022), CARD, TINT.goldChain);
    tag.position.set(0.03, 0.006, 0.026);
    tag.rotation.y = 0.5;
    g.add(tag);
    return g;
  },

  // --- electronics --------------------------------------------------------
  phone: () => {
    const g = new Group();
    g.add(offset(part(new BoxGeometry(0.073, 0.008, 0.15), CARD, TINT.phone), 0, 0.004, 0));
    // Screen, bezel gap and all, plus a camera island with two lenses.
    g.add(offset(part(new BoxGeometry(0.066, 0.0012, 0.14), CARD, 0x0b0e13), 0, 0.0086, 0));
    g.add(offset(part(new BoxGeometry(0.058, 0.0014, 0.13), CARD, 0x1b2733), 0, 0.0092, 0.002));
    const island = part(new BoxGeometry(0.03, 0.0035, 0.03), CARD, 0x22272e);
    island.position.set(-0.017, -0.0015, -0.05);
    g.add(island);
    for (const z of [-0.057, -0.044]) {
      g.add(offset(part(new CylinderGeometry(0.0055, 0.0055, 0.005, 10), DARK, 0x0a0c10), -0.017, -0.003, z));
    }
    g.add(offset(part(new BoxGeometry(0.002, 0.004, 0.02), CARD, 0x4a515a), 0.0375, 0.004, -0.02));
    return g;
  },
  laptop: () => {
    const g = new Group();
    // Base with a recessed keyboard well and a trackpad.
    g.add(offset(part(new BoxGeometry(0.3, 0.014, 0.21), CARD, TINT.laptop), 0, 0.007, 0));
    g.add(offset(part(new BoxGeometry(0.25, 0.0025, 0.11), CARD, 0x2a2f36), 0, 0.0152, -0.03));
    g.add(offset(part(new BoxGeometry(0.09, 0.002, 0.055), CARD, 0x828b95), 0, 0.0152, 0.055));
    // Key rows, drawn as strips. Individual keys at this size are invisible and
    // cost three hundred triangles to prove it.
    for (let r = 0; r < 4; r++) {
      g.add(offset(part(new BoxGeometry(0.24, 0.0022, 0.019), CARD, 0x161a1f), 0, 0.0168, -0.072 + r * 0.026));
    }
    // Lid, hinged open, with the panel inset from the bezel.
    const lid = new Group();
    lid.position.set(0, 0.012, -0.105);
    // A shade past ninety, leaning back. Sign matters: the local -Z the panel is
    // built along has to swing up to +Y, and the negative turn swung it down
    // through the floor instead — an eighteen-centimetre screen buried in tile.
    lid.rotation.x = 1.35;
    lid.add(offset(part(new BoxGeometry(0.3, 0.011, 0.2), CARD, 0x848d97), 0, 0, -0.1));
    lid.add(offset(part(new BoxGeometry(0.275, 0.0016, 0.175), CARD, 0x10141a), 0, 0.0068, -0.1));
    lid.add(offset(part(new BoxGeometry(0.262, 0.0018, 0.162), CARD, 0x1f3040), 0, 0.0072, -0.1));
    g.add(lid);
    g.add(offset(part(new CylinderGeometry(0.006, 0.006, 0.27, 8), DARK, 0x4c545e), 0, 0.012, -0.103, 0, 0, Math.PI / 2));
    return g;
  },
  television: () => {
    const g = new Group();
    // Face down would be sensible and unreadable. Leaning back on its stand.
    const panel = new Group();
    panel.rotation.x = 0.12;
    panel.position.y = 0.055;
    panel.add(offset(part(new BoxGeometry(0.92, 0.5, 0.035), CARD, TINT.television), 0, 0.26, 0));
    panel.add(offset(part(new BoxGeometry(0.885, 0.468, 0.004), CARD, 0x0d1015), 0, 0.26, 0.02));
    // The screen itself, very slightly blue — a black rectangle reads as a hole.
    panel.add(offset(part(new BoxGeometry(0.868, 0.452, 0.003), CARD, 0x1a2430), 0, 0.262, 0.023));
    // Rear boss where the electronics live, and a brand strip under the screen.
    panel.add(offset(part(new BoxGeometry(0.4, 0.24, 0.03), CARD, 0x14181e), 0, 0.19, -0.03));
    panel.add(offset(part(new BoxGeometry(0.07, 0.008, 0.004), CARD, 0x6f7883), 0, 0.026, 0.02));
    g.add(panel);
    // Two splayed feet rather than a slab.
    for (const x of [-0.3, 0.3]) {
      g.add(offset(part(new BoxGeometry(0.05, 0.055, 0.16), DARK, 0x30353c), x, 0.028, 0.02, 0, 0, x > 0 ? -0.2 : 0.2));
      g.add(offset(part(new BoxGeometry(0.09, 0.012, 0.2), DARK, 0x272b31), x, 0.006, 0.02));
    }
    return g;
  },
  drone: () => {
    const g = new Group();
    g.add(offset(part(new BoxGeometry(0.11, 0.045, 0.14), CARD, TINT.drone), 0, 0.045, 0));
    g.add(offset(part(new BoxGeometry(0.09, 0.012, 0.06), CARD, 0x1a1e24), 0, 0.072, -0.01));
    // Four arms out to four rotors, with the props sagging as unpowered props do.
    for (let i = 0; i < 4; i++) {
      const sx = i % 2 === 0 ? 1 : -1;
      const sz = i < 2 ? 1 : -1;
      g.add(offset(part(new BoxGeometry(0.14, 0.012, 0.022), CARD, 0x2a2f36), sx * 0.09, 0.045, sz * 0.09, 0, sz * sx * -0.7, 0));
      g.add(offset(part(new CylinderGeometry(0.018, 0.02, 0.028, 10), CARD, 0x1f242a), sx * 0.15, 0.05, sz * 0.15));
      for (const t of [0, Math.PI / 2]) {
        g.add(offset(part(new BoxGeometry(0.11, 0.0025, 0.014), CARD, 0x3b424a), sx * 0.15, 0.065, sz * 0.15, 0.06, t + i, 0));
      }
      g.add(offset(part(new CylinderGeometry(0.004, 0.004, 0.045, 6), CARD, 0x4a525c), sx * 0.15, 0.022, sz * 0.15));
    }
    // Gimballed camera ball hanging off the nose.
    g.add(offset(part(new SphereGeometry(0.022, 8, 6), CARD, 0x15181d), 0, 0.028, 0.072));
    g.add(offset(part(new CylinderGeometry(0.011, 0.011, 0.01, 10), GLASSY, 0x2c4a5c), 0, 0.028, 0.09, Math.PI / 2, 0, 0));
    return g;
  },
  console: () => {
    const g = new Group();
    g.add(offset(part(new BoxGeometry(0.36, 0.085, 0.3), CARD, TINT.console), 0, 0.045, 0));
    // A vent field along one flank, a disc slot, a power line and rubber feet.
    for (let i = 0; i < 9; i++) {
      g.add(offset(part(new BoxGeometry(0.006, 0.055, 0.26), CARD, 0x0d1014), -0.16 + i * 0.014, 0.045, 0));
    }
    g.add(offset(part(new BoxGeometry(0.15, 0.005, 0.006), CARD, 0x0a0c0f), 0.06, 0.05, 0.152));
    g.add(offset(part(new BoxGeometry(0.05, 0.0022, 0.004), CARD, 0x3f7fd0), 0.06, 0.089, 0.06));
    // The top is a separate, paler shell with a gap under it. A single dark box
    // photographed from above is a black rectangle and nothing else.
    g.add(offset(part(new BoxGeometry(0.34, 0.012, 0.28), CARD, 0x3a4048), 0, 0.093, 0));
    g.add(offset(part(new BoxGeometry(0.2, 0.014, 0.12), CARD, 0x14181d), 0.06, 0.1, 0));
    for (let i = 0; i < 4; i++) {
      g.add(offset(part(new CylinderGeometry(0.012, 0.012, 0.006, 8), RUBBER, 0x0e1013), (i % 2 ? 1 : -1) * 0.15, 0.003, (i < 2 ? 1 : -1) * 0.12));
    }
    // The pad, dumped next to it. Nothing else in the catalogue is a console;
    // one controller on the floor and there is no question what this is.
    const pad = new Group();
    pad.position.set(0.02, 0, 0.24);
    pad.rotation.y = 0.6;
    pad.add(offset(part(new BoxGeometry(0.1, 0.025, 0.055), CARD, 0x2c323a), 0, 0.014, 0));
    for (const x of [-0.07, 0.07]) {
      pad.add(offset(part(new BoxGeometry(0.055, 0.028, 0.05), CARD, 0x2c323a), x, 0.014, 0.012, 0, x > 0 ? -0.3 : 0.3, 0));
      pad.add(offset(part(new CylinderGeometry(0.011, 0.011, 0.008, 10), CARD, 0x15181d), x * 0.5, 0.03, 0.006));
    }
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2;
      pad.add(offset(part(new CylinderGeometry(0.005, 0.005, 0.005, 6), CARD, 0x9aa3ad), 0.058 + Math.cos(a) * 0.011, 0.03, -0.014 + Math.sin(a) * 0.011));
    }
    g.add(pad);
    return g;
  },
  espressoMachine: () => {
    const g = new Group();
    g.add(offset(part(new BoxGeometry(0.26, 0.3, 0.24), CARD, TINT.espressoMachine), 0, 0.15, 0));
    // Bean hopper and a cup rail on top. Without them the silhouette is a box,
    // and a box with a hole in the front is a microwave.
    g.add(offset(part(new CylinderGeometry(0.062, 0.042, 0.11, 10), GLASSY, 0x8d7a5e), -0.05, 0.355, -0.04));
    g.add(offset(part(new CylinderGeometry(0.043, 0.043, 0.012, 10), CARD, 0x1e232a), -0.05, 0.306, -0.04));
    g.add(offset(part(new BoxGeometry(0.22, 0.008, 0.18), STEEL, 0x9aa2ab), 0.01, 0.304, 0.03));
    for (const x of [-0.06, 0.06]) {
      g.add(offset(part(new CylinderGeometry(0.026, 0.02, 0.05, 12), CARD, 0xf0f2f4), x + 0.06, 0.333, 0.06));
    }
    // Group head with a portafilter locked into it, which is the whole read.
    g.add(offset(part(new CylinderGeometry(0.045, 0.05, 0.05, 12), STEEL, 0x9aa2ab), 0.02, 0.14, 0.13));
    g.add(offset(part(new CylinderGeometry(0.052, 0.048, 0.03, 12), STEEL, 0x8b939c), 0.02, 0.112, 0.13));
    g.add(offset(part(new CylinderGeometry(0.014, 0.018, 0.11, 8), DARK, 0x1a1d21), 0.02, 0.105, 0.2, Math.PI / 2.3, 0, 0));
    g.add(offset(part(new CylinderGeometry(0.006, 0.006, 0.02, 6), STEEL, 0x9aa2ab), 0.02, 0.09, 0.132));
    // Steam wand, drip tray and grille, a pressure gauge and a cup under it.
    g.add(offset(part(new CylinderGeometry(0.006, 0.008, 0.14, 8), STEEL, 0xa8b0b8), -0.1, 0.16, 0.1, 0.5, 0, 0.25));
    g.add(offset(part(new BoxGeometry(0.24, 0.02, 0.15), STEEL, 0x8f979f), 0, 0.012, 0.11));
    for (let i = 0; i < 7; i++) {
      g.add(offset(part(new BoxGeometry(0.21, 0.004, 0.008), STEEL, 0xa4acb4), 0, 0.024, 0.06 + i * 0.016));
    }
    g.add(offset(part(new CylinderGeometry(0.028, 0.028, 0.008, 14), CARD, 0x1e232a), 0.02, 0.24, 0.122, Math.PI / 2, 0, 0));
    g.add(offset(part(new CylinderGeometry(0.026, 0.021, 0.045, 12), CARD, 0xe8ecf0), 0.02, 0.048, 0.13));
    return g;
  },
  vacuum: () => {
    const g = new Group();
    // Cylinder body, wheels, and a hose that actually goes somewhere.
    g.add(offset(part(new CylinderGeometry(0.11, 0.11, 0.28, 14), CARD, TINT.vacuum), 0, 0.13, 0, 0, 0, Math.PI / 2));
    g.add(offset(part(new CylinderGeometry(0.09, 0.09, 0.06, 14), CARD, 0xe4e7ea), 0.16, 0.13, 0, 0, 0, Math.PI / 2));
    g.add(offset(part(new BoxGeometry(0.1, 0.03, 0.06), DARK, 0x2c2f36), -0.02, 0.24, 0));
    for (const z of [-0.1, 0.1]) {
      g.add(offset(part(new CylinderGeometry(0.045, 0.045, 0.03, 12), RUBBER, 0x191c21), -0.08, 0.045, z, Math.PI / 2, 0, 0));
    }
    // The hose, as four ribbed segments arcing away from the inlet.
    let hx = 0.2;
    let hy = 0.14;
    for (let i = 0; i < 4; i++) {
      // Starts climbing and comes back down. A hose that only ever descends from
      // the inlet is a hose buried in the floor by the second segment.
      const a = -0.7 + i * 0.5;
      g.add(offset(part(new CylinderGeometry(0.024, 0.024, 0.1, 8), RUBBER, 0x23262c), hx, hy, 0, 0, 0, Math.PI / 2 - a));
      hx += Math.cos(a) * 0.095;
      hy -= Math.sin(a) * 0.095;
    }
    // Rigid wand down to the floor head.
    g.add(offset(part(new CylinderGeometry(0.019, 0.019, 0.13, 8), CARD, 0x3a3f47), hx + 0.03, hy - 0.05, 0, 0, 0, 0.55));
    g.add(offset(part(new BoxGeometry(0.19, 0.035, 0.09), CARD, 0x2f333a), hx + 0.09, 0.018, 0));
    return g;
  },

  // --- weapons ------------------------------------------------------------
  pistol: () => {
    const g = new Group();
    // Lying on its side would be more natural and completely unreadable — a
    // pistol seen from directly above is a rectangle. It stands on its magazine
    // base, which is why every y here clears the floor by two centimetres.
    // Slide with a sighting rib and rear serrations.
    g.add(offset(part(new BoxGeometry(0.175, 0.032, 0.028), CARD, TINT.pistol), 0.005, 0.086, 0));
    g.add(offset(part(new BoxGeometry(0.16, 0.006, 0.01), CARD, 0x1a1e23), 0.005, 0.104, 0));
    for (let i = 0; i < 5; i++) {
      g.add(offset(part(new BoxGeometry(0.003, 0.026, 0.03), CARD, 0x181c21), -0.058 - i * 0.008, 0.062, 0));
    }
    g.add(offset(part(new BoxGeometry(0.006, 0.007, 0.008), CARD, 0xc8cdd4), 0.078, 0.11, 0));
    g.add(offset(part(new BoxGeometry(0.008, 0.008, 0.018), CARD, 0xc8cdd4), -0.07, 0.111, 0));
    // Frame, trigger guard drawn as three bars, grip with a checkered panel.
    g.add(offset(part(new BoxGeometry(0.15, 0.02, 0.024), CARD, 0x22272d), 0, 0.064, 0));
    g.add(offset(part(new BoxGeometry(0.006, 0.03, 0.02), CARD, 0x22272d), 0.008, 0.043, 0));
    g.add(offset(part(new BoxGeometry(0.006, 0.03, 0.02), CARD, 0x22272d), -0.045, 0.046, 0));
    g.add(offset(part(new BoxGeometry(0.055, 0.007, 0.02), CARD, 0x22272d), -0.019, 0.032, 0));
    g.add(offset(part(new BoxGeometry(0.006, 0.02, 0.008), DARK, 0x14171b), -0.012, 0.052, 0));
    g.add(offset(part(new BoxGeometry(0.042, 0.085, 0.03), CARD, 0x1e2228), -0.055, 0.048, 0, 0, 0, -0.26));
    g.add(offset(part(new BoxGeometry(0.03, 0.06, 0.032), RUBBER, 0x121519), -0.055, 0.05, 0, 0, 0, -0.26));
    g.add(offset(part(new BoxGeometry(0.046, 0.008, 0.032), CARD, 0x2b3037), -0.077, 0.016, 0, 0, 0, -0.26));
    return g;
  },
  rifle: () => {
    const g = new Group();
    // It rests on its magazine, the way a rifle sits on a bench — hence the
    // eight centimetres of air under the receiver.
    // Barrel, gas block, flash hider.
    g.add(offset(part(new CylinderGeometry(0.009, 0.011, 0.44, 10), STEEL, 0x23282e), 0.28, 0.155, 0, 0, 0, Math.PI / 2));
    g.add(offset(part(new BoxGeometry(0.03, 0.03, 0.024), STEEL, 0x2b3037), 0.13, 0.158, 0));
    g.add(offset(part(new CylinderGeometry(0.014, 0.012, 0.06, 8), STEEL, 0x1b1f24), 0.5, 0.155, 0, 0, 0, Math.PI / 2));
    // Handguard with vent slots, receiver, optic on a rail.
    g.add(offset(part(new BoxGeometry(0.26, 0.046, 0.042), CARD, 0x2f353c), 0.13, 0.155, 0));
    for (let i = 0; i < 5; i++) {
      g.add(offset(part(new BoxGeometry(0.03, 0.012, 0.045), CARD, 0x14181c), 0.05 + i * 0.045, 0.075, 0));
    }
    g.add(offset(part(new BoxGeometry(0.2, 0.062, 0.04), CARD, 0x2b3037), -0.06, 0.16, 0));
    g.add(offset(part(new BoxGeometry(0.24, 0.008, 0.02), CARD, 0x1a1e22), 0.02, 0.193, 0));
    g.add(offset(part(new CylinderGeometry(0.019, 0.019, 0.11, 12), CARD, 0x14181c), -0.05, 0.212, 0, 0, 0, Math.PI / 2));
    g.add(offset(part(new CylinderGeometry(0.017, 0.017, 0.004, 12), GLASSY, 0x3a6070), 0.008, 0.212, 0, 0, 0, Math.PI / 2));
    // Curved magazine, pistol grip, and a stock with a cut-out.
    g.add(offset(part(new BoxGeometry(0.045, 0.14, 0.03), CARD, 0x22272c), -0.055, 0.075, 0, 0, 0, 0.18));
    g.add(offset(part(new BoxGeometry(0.045, 0.11, 0.032), CARD, TINT.rifle), -0.13, 0.1, 0, 0, 0, -0.3));
    g.add(offset(part(new BoxGeometry(0.2, 0.075, 0.038), CARD, TINT.rifle), -0.26, 0.155, 0));
    g.add(offset(part(new BoxGeometry(0.1, 0.03, 0.042), CARD, 0x14181c), -0.26, 0.155, 0));
    g.add(offset(part(new BoxGeometry(0.02, 0.1, 0.04), RUBBER, 0x101317), -0.355, 0.15, 0));
    return g;
  },
  ammoCase: () => {
    const g = new Group();
    g.add(offset(part(new BoxGeometry(0.3, 0.14, 0.17), CARD, TINT.ammoCase), 0, 0.07, 0));
    // Lid with a rolled edge, a swing handle and a draw latch.
    g.add(offset(part(new BoxGeometry(0.31, 0.022, 0.18), CARD, 0x565d48), 0, 0.148, 0));
    g.add(offset(part(new BoxGeometry(0.13, 0.012, 0.014), STEEL, 0x3d4438), 0, 0.17, 0));
    g.add(offset(part(new BoxGeometry(0.014, 0.03, 0.014), STEEL, 0x3d4438), -0.06, 0.164, 0, 0, 0, -0.5));
    g.add(offset(part(new BoxGeometry(0.014, 0.03, 0.014), STEEL, 0x3d4438), 0.06, 0.164, 0, 0, 0, 0.5));
    g.add(offset(part(new BoxGeometry(0.02, 0.05, 0.03), STEEL, 0x6b7358), 0.156, 0.135, 0, 0, 0, 0.3));
    for (let i = 0; i < 3; i++) {
      g.add(offset(part(new BoxGeometry(0.006, 0.11, 0.175), CARD, 0x3f4636), -0.1 + i * 0.1, 0.07, 0));
    }
    g.add(offset(part(new BoxGeometry(0.11, 0.0016, 0.03), CARD, 0xd8d2ae), 0, 0.141, 0.06));
    return g;
  },
  longsword: () => {
    const g = new Group();
    // Blade tapering to a point, with a fuller down the middle of it.
    g.add(offset(part(new BoxGeometry(0.78, 0.0075, 0.042), STEEL, TINT.longsword), 0.15, 0.012, 0));
    g.add(offset(part(new BoxGeometry(0.7, 0.009, 0.014), STEEL, 0x7c848e), 0.13, 0.012, 0));
    g.add(offset(part(new CylinderGeometry(0.0, 0.021, 0.09, 4), STEEL, TINT.longsword), 0.58, 0.012, 0, 0, Math.PI / 4, -Math.PI / 2));
    // Crossguard, a wrapped grip drawn as bindings, and a wheel pommel.
    g.add(offset(part(new BoxGeometry(0.022, 0.026, 0.17), DARK, 0x4a4033), -0.245, 0.014, 0));
    g.add(offset(part(new BoxGeometry(0.03, 0.03, 0.03), DARK, 0x5a4a38), -0.245, 0.014, 0));
    g.add(offset(part(new CylinderGeometry(0.014, 0.016, 0.15, 8), DARK, 0x3a3026), -0.335, 0.014, 0, 0, 0, Math.PI / 2));
    for (let i = 0; i < 6; i++) {
      g.add(offset(part(new CylinderGeometry(0.0165, 0.0165, 0.006, 8), DARK, 0x2c2419), -0.28 - i * 0.022, 0.014, 0, 0, 0, Math.PI / 2));
    }
    g.add(offset(part(new CylinderGeometry(0.028, 0.028, 0.014, 10), DARK, 0x5a4a38), -0.42, 0.014, 0, 0, 0, Math.PI / 2));
    return g;
  },
  halberd: () => {
    const g = new Group();
    g.add(offset(part(new CylinderGeometry(0.017, 0.019, 1.45, 8), CARD, TINT.halberd), -0.05, 0.022, 0, 0, 0, Math.PI / 2));
    // Axe blade, top spike and rear fluke, all off one head, plus the langets
    // that strap the head to the shaft.
    g.add(offset(part(new BoxGeometry(0.19, 0.006, 0.155), STEEL, 0xa8b0ba), 0.6, 0.026, 0.09, 0, 0, 0.06));
    g.add(offset(part(new BoxGeometry(0.05, 0.008, 0.05), STEEL, 0x99a1ab), 0.53, 0.026, 0.03));
    g.add(offset(part(new CylinderGeometry(0.0, 0.017, 0.2, 4), STEEL, 0xa8b0ba), 0.76, 0.026, 0, 0, Math.PI / 4, -Math.PI / 2));
    g.add(offset(part(new BoxGeometry(0.11, 0.006, 0.07), STEEL, 0x8f97a1), 0.52, 0.026, -0.055, 0, 0.4, 0));
    for (const z of [-0.019, 0.019]) {
      g.add(offset(part(new BoxGeometry(0.22, 0.004, 0.014), STEEL, 0x6e7680), 0.5, 0.026, z));
    }
    g.add(offset(part(new CylinderGeometry(0.019, 0.019, 0.05, 8), STEEL, 0x6e7680), -0.75, 0.022, 0, 0, 0, Math.PI / 2));
    return g;
  },
  armour: () => {
    const g = new Group();
    // Still on its display stand, because that is how it is standing in the
    // window at closing time and because a cuirass lying on its side is an
    // unreadable pile of grey. Carrying a whole mounted suit out of a mall is
    // also exactly the joke the item is for.
    g.add(offset(part(new CylinderGeometry(0.13, 0.16, 0.03, 12), DARK, 0x33383f), 0, 0.015, 0));
    g.add(offset(part(new CylinderGeometry(0.025, 0.025, 0.34, 8), DARK, 0x3d434b), 0, 0.2, 0));
    // Breastplate: two boxes and a ridge, tapering to the waist.
    g.add(offset(part(new BoxGeometry(0.34, 0.26, 0.19), CARD, TINT.armour), 0, 0.49, 0));
    g.add(offset(part(new BoxGeometry(0.26, 0.12, 0.17), CARD, 0xa4acb6), 0, 0.33, 0));
    g.add(offset(part(new BoxGeometry(0.06, 0.36, 0.2), CARD, 0xc2cad4), 0, 0.44, 0.02));
    // Fauld lames hanging off the waist.
    for (let i = 0; i < 3; i++) {
      g.add(offset(part(new BoxGeometry(0.26, 0.045, 0.18), CARD, 0x8d959f), 0, 0.28 - i * 0.042, 0.004));
    }
    // Pauldrons, and a visored helm on top of the whole thing.
    for (const x of [-0.2, 0.2]) {
      g.add(offset(part(new SphereGeometry(0.1, 8, 6), CARD, 0xb6bec8), x, 0.58, 0));
      g.add(offset(part(new BoxGeometry(0.11, 0.03, 0.19), CARD, 0x9aa2ac), x, 0.53, 0));
    }
    g.add(offset(part(new SphereGeometry(0.1, 10, 7), CARD, 0xaeb6c0), 0, 0.7, 0));
    g.add(offset(part(new BoxGeometry(0.13, 0.09, 0.09), CARD, 0xb8c0ca), 0, 0.69, 0.06));
    g.add(offset(part(new BoxGeometry(0.12, 0.018, 0.02), DARK, 0x0e1116), 0, 0.71, 0.098));
    g.add(offset(part(new BoxGeometry(0.05, 0.05, 0.05), CARD, 0xc8d0da), 0, 0.78, 0, 0.4, 0, 0.78));
    return g;
  },

  // --- the rest, where a parcel is not good enough ------------------------
  safeCash: () => {
    const g = new Group();
    // Banded bricks tipped out in a heap, each brick a stack of leaves.
    const AT: Array<[number, number, number, number]> = [
      [0, 0.018, 0, 0.1],
      [0.03, 0.055, 0.02, -0.22],
      [-0.02, 0.018, 0.085, 0.42],
      [0.06, 0.018, -0.07, -0.05],
      [0.015, 0.092, 0.03, 0.3],
    ];
    for (const [x, y, z, yaw] of AT) {
      const b = part(new BoxGeometry(0.15, 0.034, 0.07), CARD, TINT.safeCash);
      b.position.set(x, y, z);
      b.rotation.y = yaw;
      g.add(b);
      // Leaf edges, so a brick is paper rather than a green block.
      for (let k = 0; k < 3; k++) {
        const leaf = part(new BoxGeometry(0.151, 0.002, 0.071), CARD, 0xcfd8c4);
        leaf.position.set(x, y - 0.012 + k * 0.011, z);
        leaf.rotation.y = yaw;
        g.add(leaf);
      }
      const band = part(new BoxGeometry(0.028, 0.036, 0.072), CARD, 0xd8b45a);
      band.position.set(x, y, z);
      band.rotation.y = yaw;
      g.add(band);
    }
    return g;
  },
  coat: () => {
    const g = new Group();
    // On its hanger, off the rail. Folded fabric is a slab from every angle;
    // a hanger is the one silhouette that says "clothing" from across a shop.
    g.add(offset(part(new BoxGeometry(0.03, 0.007, 0.26), CARD, 0x6a5a44), -0.075, 0.098, 0, 0, 0, 0.16));
    g.add(offset(part(new BoxGeometry(0.03, 0.007, 0.26), CARD, 0x6a5a44), 0.075, 0.098, 0, 0, 0, -0.16));
    g.add(offset(part(new BoxGeometry(0.16, 0.007, 0.03), CARD, 0x6a5a44), 0, 0.086, 0));
    // The hook stands clear of the fabric. Buried in it, the one silhouette that
    // says "clothing" instead of "folded blanket" may as well not be modelled.
    g.add(offset(part(new TorusGeometry(0.024, 0.0035, 4, 12), STEEL, 0x9aa2ab), 0, 0.132, 0, Math.PI / 2, 0, 0));
    // Shoulders, body, lapels, sleeves, buttons.
    g.add(offset(part(new BoxGeometry(0.26, 0.05, 0.24), CARD, TINT.coat), 0, 0.062, 0));
    g.add(offset(part(new BoxGeometry(0.3, 0.055, 0.4), CARD, TINT.coat), 0.01, 0.036, 0.02));
    g.add(offset(part(new BoxGeometry(0.24, 0.02, 0.14), CARD, 0x4a5566), 0, 0.076, -0.06));
    for (const x of [-0.08, 0.08]) {
      g.add(offset(part(new BoxGeometry(0.06, 0.035, 0.16), CARD, 0x333c49), x, 0.07, 0.02, 0, x > 0 ? 0.16 : -0.16, 0));
    }
    for (const x of [-0.17, 0.17]) {
      g.add(offset(part(new BoxGeometry(0.075, 0.04, 0.34), CARD, 0x39424f), x, 0.03, 0.05, 0, x > 0 ? -0.18 : 0.18, 0));
    }
    for (let i = 0; i < 3; i++) {
      g.add(offset(part(new CylinderGeometry(0.008, 0.008, 0.004, 8), CARD, 0x171c24), 0, 0.066, 0.03 + i * 0.07));
    }
    return g;
  },
  sneakers: () => {
    const g = new Group();
    // A pair, one tipped onto its side. A single shoe reads as a lump of bread,
    // and an all-white shoe reads as a lump of bread in a lighter colour — so
    // the upper is a mid value with a contrast panel and a dark heel counter.
    for (let s = 0; s < 2; s++) {
      const shoe = new Group();
      // The tipped one is lifted onto its own side wall — rotating a shoe about
      // its sole line puts most of it under the floor otherwise.
      shoe.position.set(s ? 0.02 : -0.02, s ? 0.1 : 0, s ? 0.075 : -0.06);
      shoe.rotation.set(s ? 1.35 : 0, s ? 0.4 : -0.15, 0);
      // Sole: a thick midsole with a dark outsole and a toe bumper.
      shoe.add(offset(part(new BoxGeometry(0.26, 0.014, 0.095), RUBBER, 0x30363d), 0, 0.007, 0));
      shoe.add(offset(part(new BoxGeometry(0.255, 0.03, 0.092), RUBBER, TINT.sneakers), 0, 0.029, 0));
      shoe.add(offset(part(new CylinderGeometry(0.024, 0.024, 0.09, 10), RUBBER, 0xf0f2f4), 0.114, 0.03, 0, Math.PI / 2, 0, 0));
      // Upper, in a value you can see against the sole.
      shoe.add(offset(part(new BoxGeometry(0.19, 0.055, 0.088), CARD, 0x455160), -0.022, 0.072, 0));
      shoe.add(offset(part(new BoxGeometry(0.07, 0.05, 0.09), CARD, 0x232b36), -0.086, 0.084, 0));
      // Toe box, tongue, a swoosh panel down each flank, laces, heel tab.
      shoe.add(offset(part(new BoxGeometry(0.075, 0.04, 0.082), CARD, 0xe8ebee), 0.072, 0.062, 0));
      shoe.add(offset(part(new BoxGeometry(0.06, 0.035, 0.06), CARD, 0xdfe3e8), 0.016, 0.096, 0, 0, 0, -0.18));
      for (const z of [-0.045, 0.045]) {
        shoe.add(offset(part(new BoxGeometry(0.12, 0.028, 0.006), CARD, 0xc84a3c), -0.01, 0.062, z, 0, 0, 0.12));
      }
      for (let i = 0; i < 3; i++) {
        shoe.add(offset(part(new BoxGeometry(0.007, 0.006, 0.058), CARD, 0xf6f7f9), 0.004 + i * 0.026, 0.104, 0));
      }
      shoe.add(offset(part(new BoxGeometry(0.016, 0.05, 0.06), CARD, 0xc84a3c), -0.118, 0.096, 0));
      g.add(shoe);
    }
    return g;
  },
  perfume: () => {
    const g = new Group();
    // Heavy glass bottle: body, shoulders, collar, a weighted cap, atomiser.
    g.add(offset(part(new BoxGeometry(0.062, 0.075, 0.032), GLASSY, TINT.perfume), 0, 0.038, 0));
    g.add(offset(part(new BoxGeometry(0.05, 0.02, 0.026), GLASSY, 0xd898b4), 0, 0.084, 0));
    g.add(offset(part(new CylinderGeometry(0.011, 0.014, 0.014, 10), CARD, 0xc8a86a), 0, 0.1, 0));
    g.add(offset(part(new CylinderGeometry(0.016, 0.016, 0.028, 10), CARD, 0x2a2118), 0, 0.121, 0));
    g.add(offset(part(new CylinderGeometry(0.017, 0.017, 0.004, 10), CARD, 0xc8a86a), 0, 0.137, 0));
    g.add(offset(part(new BoxGeometry(0.03, 0.0012, 0.02), CARD, 0xf0dfe6), 0, 0.0176, 0.017));
    return g;
  },
  firstEdition: () => {
    const g = new Group();
    // Hardback, slightly open, with a page block and a ribbon marker.
    g.add(offset(part(new BoxGeometry(0.16, 0.012, 0.23), CARD, TINT.firstEdition), 0, 0.006, 0));
    g.add(offset(part(new BoxGeometry(0.15, 0.032, 0.216), CARD, 0xe4dcc6), 0.004, 0.028, 0));
    for (let i = 0; i < 4; i++) {
      g.add(offset(part(new BoxGeometry(0.151, 0.002, 0.217), CARD, 0xd0c6ab), 0.004, 0.016 + i * 0.008, 0));
    }
    g.add(offset(part(new BoxGeometry(0.16, 0.012, 0.23), CARD, TINT.firstEdition), 0.006, 0.05, 0, 0.05, 0, 0));
    g.add(offset(part(new BoxGeometry(0.014, 0.05, 0.232), CARD, 0x63382a), -0.078, 0.028, 0));
    g.add(offset(part(new BoxGeometry(0.05, 0.0016, 0.03), CARD, 0xc8a24a), 0.01, 0.0565, -0.05));
    g.add(offset(part(new BoxGeometry(0.008, 0.001, 0.09), CARD, 0x8a2f36), 0.03, 0.0455, 0.15));
    return g;
  },
  steak: () => {
    const g = new Group();
    // Supermarket tray: polystyrene base, the cut, a fat edge, cling film, label.
    g.add(offset(part(new BoxGeometry(0.19, 0.028, 0.14), CARD, 0xf2f3f4), 0, 0.014, 0));
    g.add(offset(part(new BoxGeometry(0.165, 0.012, 0.115), CARD, 0xe4e6e8), 0, 0.026, 0));
    g.add(offset(part(new BoxGeometry(0.13, 0.026, 0.088), CARD, TINT.steak), -0.005, 0.036, 0, 0, 0.12, 0));
    g.add(offset(part(new BoxGeometry(0.132, 0.008, 0.014), CARD, 0xe8dcc4), -0.005, 0.045, 0.04, 0, 0.12, 0));
    g.add(offset(part(new BoxGeometry(0.06, 0.014, 0.05), CARD, 0x8e3634), 0.03, 0.04, -0.02, 0, -0.3, 0));
    g.add(offset(part(new BoxGeometry(0.192, 0.0016, 0.142), GLASSY, 0xf4f6f8), 0, 0.05, 0));
    g.add(offset(part(new BoxGeometry(0.07, 0.0018, 0.032), CARD, 0xfaf6e8), 0.05, 0.051, 0.045));
    return g;
  },
  skateDeck: () => {
    const g = new Group();
    // Concave deck with a kicked nose and tail, grip tape, trucks and wheels.
    g.add(offset(part(new BoxGeometry(0.56, 0.014, 0.21), CARD, TINT.skateDeck), 0, 0.062, 0));
    g.add(offset(part(new BoxGeometry(0.55, 0.0022, 0.2), RUBBER, 0x15171a), 0, 0.0702, 0));
    for (const [x, tilt] of [[0.32, -0.42], [-0.32, 0.42]] as const) {
      g.add(offset(part(new BoxGeometry(0.13, 0.014, 0.19), CARD, TINT.skateDeck), x, 0.076, 0, 0, 0, tilt));
      g.add(offset(part(new BoxGeometry(0.128, 0.0022, 0.18), RUBBER, 0x15171a), x, 0.084, 0, 0, 0, tilt));
    }
    for (const x of [-0.21, 0.21]) {
      g.add(offset(part(new BoxGeometry(0.05, 0.016, 0.09), STEEL, 0x9aa2ab), x, 0.048, 0));
      g.add(offset(part(new BoxGeometry(0.03, 0.024, 0.03), STEEL, 0x7d858e), x + (x > 0 ? 0.02 : -0.02), 0.036, 0));
      for (const z of [-0.055, 0.055]) {
        g.add(offset(part(new CylinderGeometry(0.026, 0.026, 0.03, 12), CARD, 0xe8e4d8), x, 0.026, z, Math.PI / 2, 0, 0));
      }
    }
    return g;
  },
  till: () => {
    const g = new Group();
    // Drawer hanging open with note slots and a coin tray in it — an open till
    // is the picture; a closed box is a printer.
    g.add(offset(part(new BoxGeometry(0.38, 0.11, 0.3), CARD, TINT.till), 0, 0.055, 0));
    const drawer = new Group();
    drawer.position.set(0, 0.05, 0.19);
    drawer.add(offset(part(new BoxGeometry(0.35, 0.07, 0.26), CARD, 0x2b323d), 0, 0, 0));
    drawer.add(offset(part(new BoxGeometry(0.36, 0.09, 0.016), CARD, 0x232932), 0, 0.006, 0.13));
    for (let i = 0; i < 4; i++) {
      drawer.add(offset(part(new BoxGeometry(0.006, 0.05, 0.16), CARD, 0x3c4550), -0.13 + i * 0.086, 0.024, -0.04));
      drawer.add(offset(part(new BoxGeometry(0.07, 0.012, 0.14), CARD, i % 2 ? 0x9ab08a : 0xc8bd94), -0.088 + i * 0.086, 0.02, -0.04));
    }
    drawer.add(offset(part(new BoxGeometry(0.33, 0.02, 0.06), CARD, 0x39424e), 0, 0.03, 0.085));
    g.add(drawer);
    // Raked keypad, a customer display on a post, and a receipt curling out.
    g.add(offset(part(new BoxGeometry(0.3, 0.03, 0.2), CARD, 0x4a5460), 0, 0.115, -0.03, -0.3, 0, 0));
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 4; c++) {
        g.add(offset(part(new BoxGeometry(0.05, 0.008, 0.036), CARD, r === 0 ? 0xd8dce2 : 0x8e97a2), -0.105 + c * 0.07, 0.136 + r * 0.017, -0.075 + r * 0.055, -0.3, 0, 0));
      }
    }
    g.add(offset(part(new CylinderGeometry(0.012, 0.012, 0.12, 8), CARD, 0x39424e), 0, 0.17, -0.11));
    g.add(offset(part(new BoxGeometry(0.16, 0.08, 0.014), CARD, 0x1a1f26), 0, 0.26, -0.11, 0.2, 0, 0));
    g.add(offset(part(new BoxGeometry(0.14, 0.06, 0.004), CARD, 0x2f4a3a), 0, 0.26, -0.102, 0.2, 0, 0));
    g.add(offset(part(new BoxGeometry(0.07, 0.0018, 0.09), CARD, 0xf4f2ea), 0.1, 0.132, 0.05, 0.5, 0, 0));
    return g;
  },
};

/** Position and rotate a mesh in one call. Keeps the builders one line per part. */
function offset(m: Mesh, x: number, y: number, z: number, rx = 0, ry = 0, rz = 0): Mesh {
  m.position.set(x, y, z);
  m.rotation.set(rx, ry, rz);
  return m;
}
